import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// import Restapi from '@utils/api';
import Icon from '../base_svg-icon/component';
import { routerHOC } from '@utils/hoc';
import { t } from '@utils/i18n';
import { useApp } from '../../ssr/shared/application-context';
import MyEnvironmentApi from '@utils/api/MyEnvironment.js';
import { Button } from 'audamatic-ui';
import { addToWishlistGoogleTag } from '@utils/analytics';

/**
 * Renders an 'Add to wishlist' button.
 * Two variants available;
 * - 'fullSize', for a big button (pdp)
 * - 'icon', for a simple icon (ankeiler)
 *
 * OnClick the user is redirected to the my environment-> wishlist component
 * where the sku will be added to the wishlist using the sku param in the url
 */
const WishlistToggle = (props) => {
  const { buttonType, withoutContent, store, label, product } = props;
  const appCtx = useApp();
  const sku = product.sku || store.getDefaultSku();

  const existsInWishlist = (wishlist, sku) => {
    return wishlist?.includes(sku);
  };

  const [disabled, setDisabled] = useState(false);

  /**
   * Disable the toggle until promise is resolved to prevent 'jumping' states.
   * Call API to add sku to wishlist and publish the message: 'updatedWishlist',
   * so the wishlist is fecthed again.
   * If unauthorized, trigger the login modal.
   */
  const addToWishlist = () => {
    setDisabled(true);
    setActive(true);
    const api = new MyEnvironmentApi();
    api.addToWishlist(sku)
      .then(() => {
        store.events.publish('updatedWishlist', { sku });
        store.pushToDataLayer(addToWishlistGoogleTag(product));
      })
      .catch((err) => {
        setActive(false);
        // If the user's auth token expired in the mean time
        if (err?.type === 'unauthorized') {
          openLoginModal();
        }
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  /**
   * Disable the toggle until promise is resolved to prevent 'jumping' states.
   * Call API to remove sku from wishlist and publish the message: 'updatedWishlist',
   * so the wishlist is fecthed again.
   * If unauthorized, trigger the login modal.
   */
  const removeFromWishlist = () => {
    setDisabled(true);
    setActive(false);
    const api = new MyEnvironmentApi();
    api.removeFromWishlist(sku)
      .then(() => {
        store.events.publish('updatedWishlist', { sku });
      })
      .catch((err) => {
        setActive(true);
        // If the user's auth token expired in the mean time
        if (err?.type === 'unauthorized') {
          openLoginModal();
        }
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  /**
   * Dynamically import Login component to prevent unnecessary bundling
   * and trigger a modal with the login component
   */
  const openLoginModal = () => {
    import('../sec_login-register-user/component')
      .then((component) => {
        const Login = component.default;
        store.modal.openModal('popup', '', {
          component: <Login modal={true} store={store} goNext={handleLogin}/>,
        });
      });
  };

  /**
   * Close modal if it's still open and determine whether to add
   * or remove SKU based on the current active state.
   */
  const handleLogin = () => {
    store.modal.closeModal();
    setActive(!active);
    if (!active) {
      addToWishlist();
    } else {
      removeFromWishlist();
    }
  };

  /**
   * The button item is clicked
   * Now add an item to the basket
   */
  const [active, setActive] = useState(existsInWishlist(appCtx.wishlist, sku));
  const [wishlistLength, setWishlistLength] = useState(appCtx.wishlist.lenght);
  useEffect(() => {
    if (appCtx.wishlist.length !== wishlistLength) {
      setActive(existsInWishlist(appCtx.wishlist, sku));
      setWishlistLength(appCtx.wishlist.length);
    }
  }), [appCtx.wishlist];
  const toggleState = (e) => {
    e.preventDefault();

    const { user } = store.getBasket();
    if (!user) {
      return openLoginModal();
    }

    // Only proceed is item is not disable (being updated)
    if (disabled) {
      return;
    }
    setDisabled(true);

    setActive(!active);
    if (!active) {
      addToWishlist();
    } else {
      removeFromWishlist();
    }
  };

  const wishlistLabel = label || t('wishlist.button.label');
  const activeClass = active ? 'is-active' : 'not-active';
  const icon = activeClass === 'is-active' ? <Icon name="ico-wishlist-product-added" size={150} /> : <Icon name="ico-wishlist-product-add" size={150} />;

  if (buttonType === 'fullSize') {
    return (
      <Button
        variant="secondary"
        size="large"
        iconLeft={icon}
        onClick={toggleState}
        title={wishlistLabel}
        data-test-id="button-addToWishlist"
      >
        {withoutContent ? null : wishlistLabel}
      </Button>
    );
  }

  return (
    <Button
      variant="secondary"
      size="medium"
      iconLeft={icon}
      onClick={toggleState}
      title={wishlistLabel}
      data-test-id="button-addToWishlist"
    />
  );
};

WishlistToggle.propTypes = {
  /**
   * mobile version to only show favourite icon and fullsize
   */
  withoutContent: PropTypes.bool,
  /**
   * type of the button, to determine which variant to display -> default: 'icon'
   */
  buttonType: PropTypes.oneOf(['icon', 'fullSize']),
  /**
   * Optional: Label to display on the fullSize button -> default: wishlist.button.label (Op verlanglijstje)
   */
  label: PropTypes.string,
  store: PropTypes.object,
  size: PropTypes.number,
  product: PropTypes.object,
};

export default routerHOC(WishlistToggle);
